import * as THREE from 'three';
import type { SwordFrame } from '../sword/tracking';
import {
  CUT_PROJECTION_MIN_RATIO,
  CUT_ROSE_SECTORS,
  DRILL_TYPE,
  EDGE_QUALITY,
  RAD_TO_DEG,
  TARGETS_RING_OUTER_RADIUS_M,
  TECHNIQUE,
  type EdgeQuality,
  type Zone,
} from '../config/constants';
import { sweepBladeAgainst, sphereCollider, type BladePose } from '../hit-detection/sweep';
import { edgeAlignmentAngle, classifyEdge, isThrust } from '../hit-detection/edgeAlignment';
import { projectToFrontalPlane } from '../hit-detection/frontalProjection';
import type { RuntimeSettings } from './drillRuntime';
import { formatClock } from './timer';
import { createArcadeTargetMesh, createWrongMarker } from '../targets/arcadeMesh';
import { playArcadeError } from '../feedback/audioManager';
import {
  applyCut,
  classifyCut,
  comboMultiplier,
  directionMatches,
  initialComboState,
  type ArcadeComboState,
} from './arcadeScoring';
import { aggregateArcade, type ArcadeShot } from './arcadeStats';
import { recordArcadeScore } from './arcadeBest';
import type { ArcadeStats } from './stats';
import {
  TargetDrillRuntime,
  type AliveTarget,
  type Resolution,
  type SpawnedTarget,
  type TargetDrillOptions,
} from './targetDrillRuntime';

/** Scratch for the camera world position; transient, never retained. */
const _camPos = new THREE.Vector3();

export type ArcadeCutRuntimeOptions = TargetDrillOptions & {
  /** Angular tolerance (deg) on the required cut direction. */
  cutToleranceDeg: number;
  /** Minimum tip speed (m/s) for a slice to count (below this it is noise, not a cut). */
  minSpeedMPerSec: number;
};

/** Per-target data: the required cut direction (deg, 0 = down, clockwise). */
type CutData = { requiredDeg: number };

/**
 * The arcade "Sfida" cut run: a directional-cut score attack. Each target shows a required
 * direction; a correct-direction slice scores at the current combo multiplier (by edge quality),
 * a wrong direction or a TTL timeout resets the combo. The phase machine lives in the base.
 */
export class ArcadeCutRuntime extends TargetDrillRuntime<CutData> {
  override readonly drillType = DRILL_TYPE.arcade;

  private shots: ArcadeShot[] = [];
  private comboState: ArcadeComboState = initialComboState;
  private cutToleranceDeg: number;
  private minSpeedMPerSec: number;

  constructor(opts: ArcadeCutRuntimeOptions) {
    super(opts);
    this.cutToleranceDeg = opts.cutToleranceDeg;
    this.minSpeedMPerSec = opts.minSpeedMPerSec;
  }

  applySettings(settings: Partial<RuntimeSettings>): void {
    if (settings.arcadeTtlMs !== undefined) this.ttlMs = settings.arcadeTtlMs;
    if (settings.arcadeSpawnDelayMs !== undefined) this.spawnDelayMs = settings.arcadeSpawnDelayMs;
    if (settings.arcadeCutToleranceDeg !== undefined)
      this.cutToleranceDeg = settings.arcadeCutToleranceDeg;
    if (settings.arcadeSizeFactor !== undefined) this.setSizeFactor(settings.arcadeSizeFactor);
    if (settings.arcadeMinSpeed !== undefined) this.minSpeedMPerSec = settings.arcadeMinSpeed;
  }

  protected resetRun(): void {
    this.shots = [];
    this.comboState = initialComboState;
  }

  protected spawnTarget(center: THREE.Vector3, zone: Zone): SpawnedTarget<CutData> {
    void center;
    void zone;
    const sector = Math.floor(Math.random() * CUT_ROSE_SECTORS);
    const requiredDeg = sector * (360 / CUT_ROSE_SECTORS);
    const group = createArcadeTargetMesh(requiredDeg);
    const ringMat = group.userData['ringMat'] as THREE.MeshBasicMaterial;
    const dotMat = group.userData['dotMat'] as THREE.MeshBasicMaterial;
    const discMat = group.userData['discMat'] as THREE.MeshBasicMaterial;
    const arrowMat = group.userData['arrowMat'] as THREE.MeshBasicMaterial;
    return {
      group,
      fadeMats: [ringMat, dotMat, discMat, arrowMat],
      missColorMats: [ringMat, dotMat],
      data: { requiredDeg },
    };
  }

  protected resolveFrame(
    now: number,
    frame: SwordFrame,
    prevBlade: BladePose | null,
    alive: AliveTarget<CutData>,
  ): Resolution | null {
    const cur: BladePose = { base: frame.basePos, tip: frame.tipPos };
    const speed = frame.tipVelocity.length();
    const edgeLeading =
      speed >= this.minSpeedMPerSec && !isThrust(frame.tipVelocity, frame.bladeAxis);
    if (!edgeLeading) return null;

    const collider = sphereCollider(alive.center, TARGETS_RING_OUTER_RADIUS_M * this.sizeFactor);
    const hit = sweepBladeAgainst(prevBlade, cur, collider);
    if (!hit) return null;
    return this.evaluateSlice(now, frame, speed, alive);
  }

  /** Read direction + edge quality from a slice and resolve the alive target accordingly. */
  private evaluateSlice(
    now: number,
    frame: SwordFrame,
    speed: number,
    alive: AliveTarget<CutData>,
  ): Resolution | null {
    // Cut direction = velocity component ⟂ to the blade axis, projected onto the viewer plane.
    // Undecidable direction → ignore (keep the target alive).
    const cutDir = frame.smoothedTipVelocity
      .clone()
      .sub(frame.bladeAxis.clone().multiplyScalar(frame.smoothedTipVelocity.dot(frame.bladeAxis)));
    this.baseOpts.camera.getWorldPosition(_camPos);
    const proj = projectToFrontalPlane(cutDir, alive.center, _camPos);
    if (!proj || proj.ratio < CUT_PROJECTION_MIN_RATIO) return null;

    const angleRad = edgeAlignmentAngle(frame.smoothedTipVelocity, frame.bladeAxis, frame.edgeDir);
    const quality: EdgeQuality = classifyEdge(angleRad) ?? EDGE_QUALITY.flat;
    const matched = directionMatches(proj.angleDeg, alive.data.requiredDeg, this.cutToleranceDeg);
    const outcome = classifyCut(matched, quality);

    if (outcome === 'clean' || outcome === 'sloppy') {
      this.feedback.fire({
        kind: 'cut',
        point: alive.center.clone(),
        quality: quality as Exclude<EdgeQuality, 'thrust'>,
        edgeDir: frame.edgeDir.clone(),
        cutDir: cutDir.clone(),
        speed,
      });
    }
    return this.registerOutcome(
      now,
      outcome,
      quality,
      angleRad != null ? angleRad * RAD_TO_DEG : undefined,
      alive,
    );
  }

  /** Score the resolution, log the shot, and describe the death animation for the base. */
  private registerOutcome(
    now: number,
    outcome: ArcadeShot['outcome'],
    quality: EdgeQuality,
    edgeDeg: number | undefined,
    alive: AliveTarget<CutData>,
  ): Resolution {
    const { state, pointsGained } = applyCut(this.comboState, outcome, quality);
    this.comboState = state;
    this.shots.push({
      tMs: now,
      outcome,
      pointsGained,
      comboAfter: state.combo,
      ...(edgeDeg !== undefined ? { edgeAngleDeg: edgeDeg } : {}),
    });

    const hit = outcome === 'clean' || outcome === 'sloppy';

    // A wrong-direction hit (not a TTL timeout): flash a red X over the roundel and play the
    // error cue. The X joins the dying group so it fades within the miss animation window.
    if (outcome === 'wrong') {
      const marker = createWrongMarker();
      alive.group.add(marker);
      playArcadeError();
      return { hit, extraFadeMats: [marker.userData['wrongMat'] as THREE.MeshBasicMaterial] };
    }
    return { hit };
  }

  protected resolveTimeout(now: number, alive: AliveTarget<CutData>): void {
    // Hard miss: TTL expired with no qualifying cut → combo resets.
    this.registerOutcome(now, 'timeout', EDGE_QUALITY.flat, undefined, alive);
  }

  protected aggregateStats(): ArcadeStats {
    return aggregateArcade(this.shots, TECHNIQUE.cut, this.difficulty);
  }

  protected override onSessionPersisted(): void {
    recordArcadeScore(this.comboState.score, TECHNIQUE.cut, this.difficulty);
  }

  protected renderHud(nowMs: number): void {
    this.hud.render({
      timerLabel: formatClock(this.timer.remainingMs(nowMs)),
      lastShot: null,
      arcade: {
        score: this.comboState.score,
        combo: this.comboState.combo,
        multiplier: comboMultiplier(this.comboState.combo),
      },
    });
  }
}
